import { useEffect, useRef } from 'react';

/**
 * A sphere that an editor changed: saved through its form, or changed while it stayed open (its glyph,
 * colour or picture). `revision` increases with every change, so the same sphere changing twice is
 * two changes. Hosts that open the editor over other views pass this down to those views.
 */
export interface SphereChange {
  sphereId: string;
  revision: number;
}

/**
 * Runs `onChange` once for each change that arrives after the view mounted. A change already present
 * at mount is skipped: the view's first fetch already reads the sphere as changed.
 */
export function useSphereChangeEffect(
  change: SphereChange | null | undefined,
  onChange: (sphereId: string) => void
): void {
  const seenRevisionRef = useRef(change?.revision);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!change || change.revision === seenRevisionRef.current) return;
    seenRevisionRef.current = change.revision;
    onChangeRef.current(change.sphereId);
  }, [change]);
}
